import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

const KEY = "berulia_cart";
// Mirrors FREE_SHIPPING_THRESHOLD backend-side. Only used until
// GET /shipping/rates answers — the API is the source of truth, because the
// threshold drops to 0 during a free-shipping operation and the cart must
// never quote a fee the backend will not charge.
const FALLBACK_THRESHOLD = 79;

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try { const raw = localStorage.getItem(KEY); if (raw) setItems(JSON.parse(raw)); } catch {}
  }, []);
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

  const add = (product, size, quantity = 1) => {
    setItems(prev => {
      const idx = prev.findIndex(i => i.product_id === product.id && i.size === size);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
        return copy;
      }
      return [...prev, {
        product_id: product.id,
        code: product.code,
        name: product.name,
        image: (product.images || [])[0] || "",
        unit_price: product.price,
        size,
        quantity,
      }];
    });
    setOpen(true);
  };
  const remove = (product_id, size) => setItems(prev => prev.filter(i => !(i.product_id === product_id && i.size === size)));
  const updateQty = (product_id, size, qty) => setItems(prev => prev.map(i => (i.product_id === product_id && i.size === size) ? { ...i, quantity: Math.max(1, qty) } : i));
  const clear = () => setItems([]);

  // Shipping terms come from the API: the threshold falls to 0 while a
  // free-shipping operation is running, and `promo` carries the banner copy.
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(FALLBACK_THRESHOLD);
  const [shippingPromo, setShippingPromo] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/shipping/rates").then(({ data }) => {
      if (!alive) return;
      const t = Number(data?.free_shipping_threshold);
      if (Number.isFinite(t)) setFreeShippingThreshold(t);
      setShippingPromo(data?.promo?.active ? data.promo : null);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.unit_price * i.quantity, 0), [items]);
  // Default display rate: Colissimo (the most expensive option — shown in
  // the cart drawer as "à partir de"). The Checkout page recomputes once the
  // customer picks Mondial Relay (4.90 €) or Colissimo (6.90 €), and the
  // backend authoritatively sets the final amount.
  const shipping = subtotal >= freeShippingThreshold ? 0 : (items.length ? 6.9 : 0);
  const total = subtotal + shipping;
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartCtx.Provider value={{ items, add, remove, updateQty, clear, subtotal, shipping, total, count, open, setOpen, freeShippingThreshold, shippingPromo }}>
      {children}
    </CartCtx.Provider>
  );
}
